<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue';
  import { useStore } from 'vuex';
  import { themingCssClass } from "vue/applications/frontend/mixins/theming-mixin"
  import ScoreItemText from "vue/applications/frontend/components/score-item-text.vue"
  import ScoreItemQuantity from "vue/applications/frontend/components/score-item-quantity.vue"
  import ScoreItemBubble from "vue/applications/frontend/components/score-item-bubble.vue"
  import ScoreItemShowMoreButton from "vue/applications/frontend/components/score-item-show-more-button.vue" 
  import TabbedContent from "vue/applications/frontend/components/tabbed-content.vue"
  import InfoItem from "vue/applications/frontend/components/info-item.vue"

  const props = defineProps({
    scoreItem: {
      type: Object,
    },
  });

  const store = useStore();
  const currentValue = ref<any>(null);
  const currentDateFrom = ref<any>(null);
  const currentDateTo = ref<any>(null);
  const values = ref<Array<{key: number, value: number, label: number}>> ([
    { key: 1, value: 1, label: 1 },
    { key: 2, value: 2, label: 2 },
    { key: 3, value: 3, label: 3 },
    { key: 4, value: 4, label: 4 },
  ]);
  const showMore = ref<boolean>(false);
  const isCollapsed = ref<boolean>(true);

  onMounted(() => {
    if (props.scoreItem) {
      currentValue.value = props.scoreItem.ivalue;
      if(props.scoreItem.free_text) currentValue.value = currentValue.value ?? props.scoreItem.previous_scores[0]?.value
      currentDateFrom.value = props.scoreItem.date_from ?? null;
      currentDateTo.value = props.scoreItem.date_to ?? null;
      // Entrust display: the WBB enters nothing; submit 0 so the score persists and the exam stays valid.
      if(isEntrustDisplay.value) currentValue.value = props.scoreItem.ivalue ?? 0;
    }
  });

  const name = computed(() => {
    if (props.scoreItem && currentValue.value != null) {
      return `${props.scoreItem.name_prefix}[${props.scoreItem.id}]`;
    }
    return null;
  });
  const lastPreviousScore = computed(() => {
    if (props.scoreItem && props.scoreItem.skip_last_previous_score)
      return null;
    const previousScores = props.scoreItem?.previous_scores || [];
    if (previousScores.length > 0) {
      return previousScores[0];
    }
    return null;
  });
  const filtered = computed(() => {
    // filtering is not even active
    if (!store.getters.filteringActive) {
      return false;
    }

    // Dont filter out required items
    if ((props.scoreItem || {}).required) {
      return false;
    }

    // get filters preprocessed from store
    const filtersFromStoreAsIDChecked = store.getters.filterIDToChecked;

    // We dont have previous scores
    if (((props.scoreItem || {}).previous_scores || []).length <= 0) {
      return !filtersFromStoreAsIDChecked[-1];
    }

    // Get last score from previous scores
    const lastScore = [...props.scoreItem?.previous_scores].reverse().pop();

    // get correctFilter
    const correctFilter =
      filtersFromStoreAsIDChecked[lastScore.score_level_id];

    if (correctFilter == null) {
      // undefined or null
      return false;
    }

    return !correctFilter;
  });
  const collapsible = computed(() => !props.scoreItem?.readonly && props.scoreItem?.is_collapsed);
  const collapsed = computed(() => collapsible.value && isCollapsed.value);
  const hasAdditionalInfo = computed(() => (props.scoreItem?.info_items || []).length > 0)
  const moreLabel = computed(() => {
    if (!props.scoreItem?.cumulate_scores) return null;  // don't show history if scores are not cumulatd
    if(hasAdditionalInfo.value && (props.scoreItem?.show_all_previous_values || !usesPreviousValues.value) ) return "weitere Informationen";
    if(hasAdditionalInfo.value && usesPreviousValues.value) return "letzte Einträge / weitere Informationen";
    if(usesPreviousValues.value) return "letzte Einträge";
    return null;
  });
  const isQuantity = computed(() => props.scoreItem?.item_type === 'quantity');
  // WBB autonomous Bogen: read-only count that submitting will entrust (no input).
  const entrustQuantity = computed(() => props.scoreItem?.entrust_quantity);
  const isEntrustDisplay = computed(() => entrustQuantity.value !== null && entrustQuantity.value !== undefined);
  // Read-only Mengen-Feld without any documented data (e.g. the blank Musterbogen the
  // WBB sees): the input prompt and its empty fields say nothing, so hide the whole item.
  const isEmptyReadonlyQuantity = computed(() =>
    isQuantity.value &&
    !isEntrustDisplay.value &&
    !!props.scoreItem?.readonly &&
    currentValue.value == null &&
    !currentDateFrom.value &&
    !currentDateTo.value
  );
  const layout = computed(() => (props.scoreItem?.free_text || isQuantity.value) ? 'stacked-layout' : 'left-right-layout');
  const transmittedValue = computed(() => {
    if(props.scoreItem?.free_text && lastPreviousScore.value?.value === currentValue.value) return null;
    else return currentValue.value;
  });
  const usesPreviousValues = computed(() => !!usesUserInput.value)
  const showAllPreviousValues = computed(() => !!props.scoreItem?.show_all_previous_values && !!props.scoreItem.free_text);
  const usesUserInput = computed(() => !!props.scoreItem?.user_input)
  const shownSyncedScores = computed(() => {
    if(!props.scoreItem?.synced_scores) return [];

    return props.scoreItem.synced_scores.map((scoreItem: any) => ({
      ...scoreItem,
      scores: scoreItem.scores.length > 0 ? scoreItem.scores[0] : null,
    }));
  });

  const selectItem = (value: any) => {
    if (props.scoreItem?.readonly) return;
    else if (value && value.value === currentValue.value) {
      // already selected?
      currentValue.value = null;
    } else if (value) {
      currentValue.value = value.value;
    } else currentValue.value = null;
  };
</script>

<template>
  <div class="components__score-item print--avoid-page-break" v-if="!isEmptyReadonlyQuantity">
    <div
      class="hueish"
      v-bind:class="[{ filtered }, themingCssClass('light_background')]"
    >
      <input
        v-if="usesUserInput && name"
        type="hidden"
        v-bind:name="name"
        v-bind:value="transmittedValue"
      />
      <template v-if="isQuantity && name && !isEntrustDisplay">
        <input
          type="hidden"
          v-bind:name="`score_item_date_from[${scoreItem.id}]`"
          v-bind:value="currentDateFrom"
        />
        <input
          type="hidden"
          v-bind:name="`score_item_date_to[${scoreItem.id}]`"
          v-bind:value="currentDateTo"
        />
      </template>
      <template v-if="collapsed">
        <div
          class="text collapse-text"
          v-bind:class="[themingCssClass('normal')]"
          v-on:click="isCollapsed = !isCollapsed"
        >
          <p class="collapse-text__label">
            {{ scoreItem.text }}
          </p>
          einblenden
          <template v-if="transmittedValue">
            <br/>
            (bereits bearbeitet)
          </template>
        </div>
      </template>
      <template v-else-if="(filtered)">
        <div
          class="text filter-text"
          v-bind:class="[themingCssClass('normal')]"
        >
          Dieses Element wurde aufgrund des gewählten Filters ausgeblendet.
      </div>
      </template>
      <template v-else>
        <component v-bind:is="layout">
          <template v-slot:label>
            <div class="text" v-if="scoreItem.text !== '.'">
              {{ scoreItem.text }}
              <template v-if="scoreItem.required">*</template>
              <div v-if="collapsible"
                class="text collapse-text"
                v-bind:class="[themingCssClass('normal')]"
                v-on:click="isCollapsed = !isCollapsed">
                ausblenden
              </div>
            </div>
          </template>
          <template v-if="usesUserInput" v-slot:user-input>
            <div v-if="isEntrustDisplay" class="entrust-display">
              <template v-if="entrustQuantity > 0">Es werden {{ entrustQuantity }} dokumentierte Einheit(en) anvertraut.</template>
              <template v-else>Keine offenen Mengen zum Anvertrauen.</template>
            </div>
            <ScoreItemQuantity v-else-if="isQuantity" v-model="currentValue" v-model:date-from="currentDateFrom" v-model:date-to="currentDateTo" v-bind:readonly="scoreItem.readonly" />
            <ScoreItemText v-else-if="scoreItem.free_text" v-model="currentValue" v-bind:readonly="scoreItem.readonly" />
            <template v-for="value in (scoreItem.previous_scores || []).slice(1)" v-bind:key="value.id">
               <ScoreItemText v-if="showAllPreviousValues" v-model="value.value" v-bind:readonly="scoreItem.readonly" />
            </template>
            <template  v-for="scoreLevel in scoreItem.score_levels"  v-bind:key="'user-input--' + scoreLevel.id">
              <ScoreItemBubble
                class="bubble bubble--user-input"
                v-if="scoreItem.score_levels"
                v-on:trigger="selectItem(scoreLevel)"
                v-bind:score-level="scoreLevel"
                v-bind:current-value="currentValue"
              />
            </template>
          </template>
          <template v-if="scoreItem.keywords_html" v-slot:keywords>
            <span class="keywords" v-html="scoreItem.keywords_html"></span>
          </template>
          <template v-if="lastPreviousScore" v-slot:previous-input>
            <ScoreItemText v-if="scoreItem.free_text && lastPreviousScore.value !== currentValue" label="Letzter Eintrag" readonly v-model="lastPreviousScore.value" light/>
            <template v-for="scoreLevel in scoreItem.score_levels" v-bind:key="'previous-input--' + scoreLevel.id">
              <ScoreItemBubble
                class="bubble bubble--previous-input"
                light
                v-if="scoreItem.score_levels"
                v-bind:score-level="scoreLevel"
                v-bind:current-value="lastPreviousScore.value"
                v-bind:hidden-if-inactive="true"
                tooltip-prefix="Letzter Eintrag: "
              />
            </template>
          </template>
          <template v-for="(si, index) in shownSyncedScores" v-slot:[`synced-input-${si.score_item_id}`]  v-bind:key="index">
            <template v-if="si.scores">
              <ScoreItemText v-if="si.free_text" label="Externe Einschätzung" readonly v-model="si.scores.value" light/>
              <template v-for="scoreLevel in si.score_levels" v-bind:key="'previous-input--' + scoreLevel.id">
                <ScoreItemBubble
                  class="bubble bubble--synced-input"
                  black
                  v-if="si.score_levels"
                  v-bind:score-level="scoreLevel"
                  v-bind:current-value="si.scores.value"
                  v-bind:hidden-if-inactive="true"
                  tooltip-prefix="Externe Einschätzung: "
                />
              </template>
            </template>
          </template>
          <template v-slot:more-button v-if="moreLabel">
            <ScoreItemShowMoreButton
              class="more-button print--hide"
              v-bind:label="moreLabel"
              v-model="showMore"
            />
          </template>
        </component>
      </template>
    </div>
    <template v-if="!filtered">
      <div class="more-text" v-bind:class="{ active: showMore }">
        <TabbedContent
          v-bind:tab-headers="{
            last: 'letzte Einträge',
            information: 'Weitere Informationen',
          }"
        >
          <template v-slot:last v-if="usesPreviousValues && !showAllPreviousValues">
            <div class="tab-content">
              <template v-if="(scoreItem.previous_scores || []).length > 0">
                <div class="previous-scores"
                v-bind:class="{'previous-scores--free-text': scoreItem.free_text, 'previous-scores--quantity': isQuantity}">
                  <div
                    class="previous-score-container"
                    v-for="previousScore in scoreItem.previous_scores"
                    v-bind:class="{'previous-score-container--free-text': scoreItem.free_text, 'previous-score-container--quantity': isQuantity}"
                    v-bind:key="previousScore.id"
                  >
                    <div class="previous-score-bubble"
                    v-bind:class="{'previous-score-bubble--free-text': scoreItem.free_text, 'previous-score-bubble--quantity': isQuantity}">
                      <ScoreItemText readonly v-if="scoreItem.free_text" v-model="previousScore.value" light/>
                      <!-- Mengen-Score-Items have no score level, so a bubble would stay empty:
                           show the documented count with the period it was recorded for. -->
                      <template v-else-if="isQuantity">
                        <span class="previous-score-quantity__count">Anzahl {{ previousScore.value }}</span>
                        <span class="previous-score-quantity__period" v-if="previousScore.period">Zeitraum {{ previousScore.period }}</span>
                        <span class="previous-score-quantity__exam">Bogen vom {{ previousScore.exam_taken_at }}</span>
                      </template>
                      <ScoreItemBubble v-else v-bind:score-level="previousScore" />
                    </div>
                    <div v-if="!isQuantity">{{ previousScore.exam_taken_at }}</div>
                  </div>
                </div>
              </template>
              <template v-else>Bisher noch nicht bewertet</template>
            </div>
          </template>
          <template v-if="hasAdditionalInfo" v-slot:information>
            <div class="tab-content">
              <ul>
                <li
                  v-for="infoItem in scoreItem.info_items"
                  v-bind:key="infoItem.id"
                >
                  <InfoItem v-bind:info-item="infoItem"></InfoItem>
                </li>
              </ul>
            </div>
          </template>
        </TabbedContent>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use "~stylesheets/frontend/variables/colors";
@import "~include-media/dist/include-media";

.entrust-display {
  margin: 10px 0;
  font-size: 16px;
  font-weight: bold;
}

$breakpoints: (
  "tablet": 1000px,
);

.components__score-item {
  .hueish {
    padding: 19px;
    border-bottom: 2px solid colors.$color-white;
  }

  .filter-text, .collapse-text{
    font-size: 12px;
    line-height: 12px;
  }

  .collapse-text {
    cursor: pointer;
  }

  .free-text {
    margin-top: 20px;
    margin-bottom: 20px;

    width: 100%;
    resize: none;
  }

  .bubble {
    margin-right: 16px;
    margin-left: 16px;

    &:first-of-type {
      margin-left: 0;
    }
    &:last-of-type {
      margin-right: 0;
    }

    @include media("<tablet") {
      margin-top: 20px;
      margin-bottom: 20px;
    }

    @media print {
      margin-top: 10px !important;
      margin-bottom: 0 !important;
      margin-right: 0 !important;
    }
  }

  .bubble--user-input {
    margin-bottom: 20px;
  }

  .text {
    @media print {
      font-size: 14px;
    }
  }

  .more-text {
    display: none;
    padding: 24px;

    &.active {
      display: block;
    }

    .tab-content {
      font-size: 16px;
      color: colors.$color-default;

      .previous-scores {
        display: flex;
        flex-wrap: wrap;
        max-width: 820px;

        &--free-text {
          display: block;
          max-width: 100%;
          width: 100%;
        }

        // one line per documented quantity, so count, period and Bogen date stay readable
        &--quantity {
          display: block;
          max-width: 100%;
          width: 100%;
        }

        .previous-score-container {
          display: flex;
          align-items: center;
          width: 200px;
          margin-top: 5px;
          margin-bottom: 5px;

          &--free-text {
            width: 100%;
            flex-direction: row-reverse;
            justify-content: start;
          }

          &--quantity {
            width: 100%;
            flex-wrap: wrap;
          }

          .previous-score-bubble {
            margin-right: 10px;

            &--free-text {
              margin-left: 10px;
            }

            &--quantity {
              display: flex;
              flex-wrap: wrap;

              .previous-score-quantity__count {
                font-weight: bold;
                margin-right: 10px;
                white-space: nowrap;
              }

              .previous-score-quantity__period,
              .previous-score-quantity__exam {
                margin-right: 10px;
              }
            }
          }
        }
      }
    }
  }
}

.item--edited {
  > i { font-size: 16px; }
  margin-top: 10px;
  align-content: center;
}

.keywords {
    display: block;

    ::v-deep * {
      margin-top: 0;
      margin-bottom: 0;
      font-size: 16px;
      line-height: 26px;
    }
  }
</style>
